import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ConfigProvider, theme } from 'antd';

export type ThemeMode = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'korsmet-theme-mode';

interface ThemeModeContextValue {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

const readStoredMode = (): ThemeMode => {
  if (typeof window === 'undefined') {
    return 'system';
  }
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return stored === 'light' || stored === 'dark' ? stored : 'system';
};

const prefersDark = () =>
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-color-scheme: dark)').matches;

/**
 * 暗色模式。默认跟随系统，用户显式选择后记到 localStorage。
 *
 * antd 的 configProvider 在 config.ts 里是静态配置，切不了算法，
 * 所以在 rootContainer 里再套一层 ConfigProvider —— 内层的 algorithm 生效。
 */
export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [systemDark, setSystemDark] = useState(prefersDark);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    if (typeof window !== 'undefined') {
      if (next === 'system') {
        window.localStorage.removeItem(STORAGE_KEY);
      } else {
        window.localStorage.setItem(STORAGE_KEY, next);
      }
    }
  }, []);

  const isDark = mode === 'dark' || (mode === 'system' && systemDark);

  // 让登录页等未被 ProLayout 包裹的页面也有正确的底色。
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.dataset.theme = isDark ? 'dark' : 'light';
    }
  }, [isDark]);

  const value = useMemo(() => ({ mode, isDark, setMode }), [mode, isDark, setMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ConfigProvider
        theme={{
          algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
        }}
      >
        {/* 仓库里存在两份 @types/react：根 node_modules 是 18.x（被根 package.json
            多余声明的 @ant-design/pro-components 带进来的），apps/frontend-pro 是 19.x。
            19 的 ReactNode 多了 bigint，跨版本传递会报不兼容。
            ponytail: 根治是把 @ant-design/pro-components 从根 package.json 移除后重装，
            让整棵树只剩一份 React 类型；在那之前先在这唯一的交界处收口。 */}
        {children as any}
      </ConfigProvider>
    </ThemeModeContext.Provider>
  );
};

export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }
  return context;
};
